import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';

export interface ServerNow {
  now: string;
  wallClock: string;
  simActive: boolean;
}

// Module-level snapshot of the last successful GET /api/now — lets pure
// (non-hook) helpers like sessionTime.ts / bookkeeping/lib/period.ts read
// "today" without needing to be React components.
let lastServerNow: Date | null = null;

/** Non-hook reader for pure helpers. Falls back to the browser clock until the first fetch resolves. */
export function serverNow(): Date {
  return lastServerNow ?? new Date();
}

/** Whether a real `GET /now` snapshot has resolved yet — lets a caller like
 *  `lib/houseToday.ts` tell "the wall-clock instant" apart from "the
 *  fallback browser clock", since only the former is safe to read with UTC
 *  getters (see that file's own comment). */
export function hasServerNowSnapshot(): boolean {
  return lastServerNow !== null;
}

function setLastServerNow(d: Date): void {
  lastServerNow = d;
}

/**
 * The dashboard's notion of "today" — follows the (possibly dev-simulated)
 * server clock instead of the browser's own `new Date()`. While loading or
 * on error, falls back to the browser clock so the UI never blocks on this.
 *
 * In dev, polls so the UI reflects time-travel via the dev clock widget; in
 * prod it's a single fetch (real time never needs refetching for this).
 */
export function useServerNow(): Date {
  const { data } = useQuery({
    queryKey: ['now'],
    queryFn: () => fetchApi<ServerNow>('/now'),
    refetchInterval: import.meta.env.DEV ? 5000 : false,
    staleTime: import.meta.env.DEV ? 0 : Infinity,
  });

  // All 6 FE "today"/"this week"/"current month" sites floor via UTC-getters
  // against session/invoice fields stored "wall-clock-as-UTC" (see
  // packages/shared-types/src/time.ts) — so they need the VN wall-clock
  // instant (`wallClock`), not the raw UTC instant (`now`). Using `now` here
  // would reproduce the WorkbenchPage wrong-day-in-VN-morning bug.
  const parsed = data ? new Date(data.wallClock) : null;
  const resolved = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;

  // Keep the module-level snapshot (read by non-hook helpers) in sync as a
  // side effect, not during render, to satisfy the react-compiler purity rule.
  useEffect(() => {
    if (resolved) setLastServerNow(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the primitive wallClock string, not the `resolved` Date (a new instance each render)
  }, [data?.wallClock]);

  return resolved ?? serverNow();
}
